import styled, { css } from 'styled-components';
import { Button } from 'src/components';

export const Screen = styled.main(
  ({ theme }) => css`
    --portrait: 8.25rem;
    --card: 9.375rem;
    --chip: 2rem;
    --chip-icon: 1.125rem;

    box-sizing: border-box;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    background: ${theme.co.neutral99};
    color: ${theme.co.secondary20};
    position: relative;

    @media (max-width: ${theme.sz.lg}) {
      --portrait: 6.5rem;
      --card: 8.25rem;
      --chip: 1.875rem;
      --chip-icon: 1rem;
    }
  `,
);

export const Logo = styled.div(
  ({ theme }) => css`
    position: absolute;
    left: ${theme.sz.s8};
    top: ${theme.sz.s6};
    font-size: 2.25rem;
    line-height: 1;
    color: ${theme.co.primary20};

    @media (max-width: ${theme.sz.lg}) {
      display: none;
    }
  `,
);

export const Body = styled.div(
  ({ theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.sz.s8};
    padding: ${theme.sz.s10} ${theme.sz.s16} 0;
    min-height: 0;

    @media (max-width: ${theme.sz.lg}) {
      justify-content: flex-start;
      align-items: stretch;
      gap: ${theme.sz.s4};
      padding: ${theme.sz.s3} ${theme.sz.s7} ${theme.sz.s4};
      overflow: auto;
    }
  `,
);

export const Header = styled.header(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: ${theme.sz.s2};
    width: min(100%, 40rem);

    @media (max-width: ${theme.sz.lg}) {
      align-items: flex-start;
      text-align: left;
      gap: 0.375rem;
      width: 100%;
    }
  `,
);

export const Title = styled.h1(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.accent.xl};
    font-size: 3.25rem;
    line-height: 1.1;
    color: ${theme.co.secondary20};
    margin: 0;
    text-wrap: pretty;

    @media (max-width: ${theme.sz.lg}) {
      ${theme.typography.heading.mobile.accent.xl};
      line-height: 1.15;
    }
  `,
);

export const Subtitle = styled.p(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-size: 1.0625rem;
    color: ${theme.co.secondary50};
    margin: 0;
    text-wrap: pretty;

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.9375rem;
    }
  `,
);

export const Rule = styled.div(
  ({ theme }) => css`
    height: ${theme.bo.mediumWidth};
    width: 100%;
    margin-top: ${theme.sz.s2};
    border-radius: ${theme.bo.round};
    background: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.lg}) {
      margin-top: ${theme.sz.s2};
    }
  `,
);

export const Grid = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(6, var(--card));
    gap: ${theme.sz.s6};
    justify-content: center;
    justify-items: center;

    @media (max-width: 71.875rem) {
      grid-template-columns: repeat(3, var(--card));
    }

    @media (max-width: ${theme.sz.lg}) {
      grid-template-columns: repeat(2, var(--card));
      gap: ${theme.sz.s2} ${theme.sz.s6};
    }
  `,
);

const cardColumn = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: var(--card);
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: center;
`;

export const LocalButton = styled.button(
  ({ theme }) => css`
    ${cardColumn};
    gap: ${theme.sz.s3};
    cursor: pointer;

    @media (max-width: ${theme.sz.lg}) {
      gap: 0.375rem;
    }

    &:focus-visible {
      outline: none;
      ${theme.focusVisible};
    }
  `,
);

export const LockedCard = styled.div(
  ({ theme }) => css`
    ${cardColumn};
    gap: ${theme.sz.s3};

    @media (max-width: ${theme.sz.lg}) {
      gap: ${theme.sz.s2};
    }
  `,
);

export const Portrait = styled.span<{ $tilt: number }>(
  ({ $tilt }) => css`
    position: relative;
    width: var(--portrait);
    height: var(--portrait);
    flex: none;
    transform: rotate(${$tilt}deg);
  `,
);

export const Ring = styled.span<{ $selected: boolean }>(
  ({ theme, $selected }) => css`
    position: absolute;
    inset: -0.4375rem;
    border-radius: ${theme.bo.memrise};
    background: ${$selected ? theme.co.primary20 : 'transparent'};
    transition: background 150ms ease;
  `,
);

export const Photo = styled.span(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    border-radius: ${theme.bo.memrise};
    overflow: hidden;
    background: ${theme.co.primary90};

    img,
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      pointer-events: none;
    }
  `,
);

export const Chip = styled.span<{ $selected: boolean; $tilt: number }>(
  ({ theme, $selected, $tilt }) => css`
    position: absolute;
    right: -0.125rem;
    bottom: -0.125rem;
    width: var(--chip);
    height: var(--chip);
    border-radius: ${theme.bo.elliptical};
    background: ${$selected ? theme.co.primary20 : theme.co.neutral99};
    border: 3px solid ${theme.co.neutral99};
    box-shadow: ${theme.sh.s1};
    display: grid;
    place-items: center;
    color: ${$selected ? theme.co.secondary0 : theme.co.secondary20};
    font-size: var(--chip-icon);
    transform: rotate(${-$tilt}deg);
    transition: background 150ms ease;
  `,
);

export const LockedPortrait = styled.span(
  ({ theme }) => css`
    width: var(--portrait);
    height: var(--portrait);
    box-sizing: border-box;
    border-radius: ${theme.bo.memrise};
    border: 3px dashed ${theme.co.neutral90};
    background: ${theme.co.neutral98};
    display: grid;
    place-items: center;
    color: ${theme.co.secondary50};
    font-size: var(--chip);
    opacity: 1;

    span {
      opacity: 0.55;
    }
  `,
);

export const Copy = styled.span(
  () => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
  `,
);

export const Name = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.base};
    font-weight: ${theme.DEPRECATED_ty.bold};
    line-height: 1.3;
    color: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.9375rem;
    }
  `,
);

export const City = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.semiBold};
    line-height: 1.3;
    color: ${theme.co.secondary50};
  `,
);

export const Fact = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    line-height: 1.4;
    color: ${theme.co.secondary40};
    text-wrap: pretty;

    @media (max-width: ${theme.sz.lg}) {
      font-size: 0.6875rem;
      line-height: 1.35;
    }
  `,
);

export const LockedLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    font-weight: ${theme.DEPRECATED_ty.semiBold};
    line-height: 1.35;
    color: ${theme.co.secondary50};
    text-wrap: pretty;
  `,
);

export const Footer = styled.footer(
  ({ theme }) => css`
    flex: none;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${theme.sz.s3};
    padding: ${theme.sz.s5} 0 ${theme.sz.s8};

    @media (max-width: ${theme.sz.lg}) {
      flex-direction: column-reverse;
      gap: 0.375rem;
      padding: ${theme.sz.s3} ${theme.sz.s6} ${theme.sz.s7};
    }
  `,
);

export const ContinueWrap = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;

    @media (max-width: ${theme.sz.lg}) {
      width: 100%;

      & > div {
        width: 100%;
      }
    }
  `,
);

export const ContinueButton = styled(Button)(
  ({ theme }) => css`
    border-radius: ${theme.bo.round};
    padding-left: 2.25rem;
    padding-right: 2.25rem;

    @media (max-width: ${theme.sz.lg}) {
      width: 100%;
      padding-left: ${theme.sz.s6};
      padding-right: ${theme.sz.s6};
    }
  `,
);

export const NotNowButton = styled(Button)(
  ({ theme }) => css`
    border-radius: ${theme.bo.round};
    color: ${theme.co.secondary50};
    background: transparent;
    box-shadow: none;

    @media (max-width: ${theme.sz.lg}) {
      min-height: ${theme.sz.s10};
      ${theme.typography.button.s};
    }
  `,
);
